// useDashPlayer.js - Custom Hook
import * as dashjs from "dashjs";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { setCurrentTimeValue, setEndTime, setPlaying } from "../redux/JobModule/QCDetailPage/QCDetailPageAction";
import { RootState } from "../redux/store";
import { useAppDispatch } from "../redux/useAppDispatch";
import { useBindKeys } from "./useBindKeys";

const useDashPlayer = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const dispatch = useAppDispatch();

  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [mute, setMute] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const mediaToken = useSelector((state: RootState) => state.qCDetailPageState.mediaToken);
  const drmToken = useSelector((state: RootState) => state.qCDetailPageState.drmToken);
  const currentTime = useSelector((state: RootState) => state.qCDetailPageState.currentTime);
  const isPlaying = useSelector((state: RootState) => state.qCDetailPageState.isPlaying);
  const [firstEnter, setFirstEnter] = useState(true);
  const src = useSelector((state: RootState) => state.qCDetailPageState.data.transcriptions[0].encryptedUrl);
  // Key Bindings for the player
  const setIsPlaying = (value: boolean) => {
    dispatch(setPlaying(value));
  };

  useEffect(() => {
    if (!audioRef.current) {
      console.error("audioRef is not initialized");
      return;
    } else if (audioRef.current) {
      //   playerRef.current = dashjs.MediaPlayer().create();
      const MediaPlayer = dashjs.MediaPlayer();
      const player = MediaPlayer.create();

      player.extend(
        "RequestModifier",
        function () {
          return {
            modifyRequestURL: function (url: string) {
              /* Modify url adding a custom query string parameter */
              return url + "?" + `access_token=${mediaToken.access_token}&issued_date=${mediaToken.issued_date}`;
            },
          };
        },
        true
      );

      player.initialize(audioRef.current, src, false);
      //   player.updateSettings({
      //     debug: {
      //       logLevel: 1,
      //     },
      //   });
      player.on(dashjs.MediaPlayer.events.PLAYBACK_ENDED, () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
      player.setProtectionData({
        "com.widevine.alpha": {
          serverURL: "https://williams-lea.la.drm.cloud/acquire-license/widevine?brandGuid=b5b369c6-20d1-4afb-afc2-a343b2417c72",
          httpRequestHeaders: {
            "X-Drm-UserToken": drmToken.jwt,
          },
          priority: 0,
        },
      });
      audioRef.current.addEventListener("timeupdate", () => {
        if (audioRef.current?.currentTime) {
          setCurrentTime(audioRef.current!.currentTime);
        }
      });

      audioRef.current.addEventListener("loadedmetadata", () => {
        if (audioRef.current?.duration) {
          setDuration(audioRef.current!.duration);
          dispatch(setEndTime(audioRef.current!.duration));
        }
      });
      audioRef.current.muted = mute;

      return () => {
        player?.reset();
      };
    }
  }, [src]);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.currentTime = currentTime;
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);
  const setCurrentTime = (time: number) => {
    dispatch(setCurrentTimeValue(time));
  };
  // useEffect(() => {
  //   console.log("calling this seek time ");
  //   if (audioRef.current) {
  //     if (firstEnter) {
  //       setFirstEnter(false);
  //       return;
  //     }
  //     audioRef.current.currentTime = seekCurrentTime;
  //     setCurrentTime(audioRef.current.currentTime);
  //     audioRef.current.play();
  //     setIsPlaying(true);
  //   }
  // }, [seekCurrentTime]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const seekAudio = (e: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = e;
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const changeVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      audioRef.current.volume = Number(e.target.value);
      setVolume(audioRef.current.volume);
    }
  };

  const changePlaybackRate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = Number(e.target.value);
      setPlaybackRate(audioRef.current.playbackRate);
    }
  };

  const skip = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime += seconds;
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const reverseSkip = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime -= seconds;
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const muteUnMute = () => {
    if (audioRef.current) {
      if (mute === false) {
        setMute(true);
      } else {
        setMute(false);
      }
    }
  };
  const changePlaybackRateSpeed = (value: number) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = audioRef.current.playbackRate + value;
      setPlaybackRate(audioRef.current.playbackRate);
    }
  };

  useBindKeys(() => togglePlay(), ["Space"], [isPlaying], true, false);
  useBindKeys(() => reverseSkip(1), ["ArrowLeft"], [], true, false);
  useBindKeys(() => skip(1), ["ArrowRight"], [], true, false);
  useBindKeys(() => muteUnMute(), ["KeyM"], [volume, mute], true, true);
  useBindKeys(() => changePlaybackRateSpeed(-0.5), ["ArrowDown"], [playbackRate], true, true);
  useBindKeys(() => changePlaybackRateSpeed(0.5), ["ArrowUp"], [playbackRate], true, true);
  return {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackRate,
    togglePlay,
    seekAudio,
    changeVolume,
    changePlaybackRate,
    skip,
    mute,
  };
};

export default useDashPlayer;
